/*eslint-disable react-hooks/rules-of-hooks */
import '../ProductCard.scss';
import './MobileProductCard.scss';

import classnames from 'classnames';
import React, { useContext, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import loadable from '@loadable/component';

import { listerClickThrough } from '../../../actions/lister-actions';
import { useIsBelowXSmallOnly } from '../../../hooks/mediaQueries';
import { useEventListener } from '../../../hooks/useEventListener';
import { useIsProductInHighDemand } from '../../../hooks/useIsProductInHighDemand';
import { useTheme } from '../../../hooks/useTheme';
import TRANSLATIONS from '../../../translations/translations@default.json';
import {
	getDeliveryDate,
	getDeliveryDaysDifference,
} from '../../../utilities/lister-utilities';
import { isProductInTradingRange } from '../../../utilities/product-utilities';
import { Context } from '../../Views/store/store';
import Bullets from '../Bullets';
import EnergyInformation from '../EnergyInformation/EnergyInformation';
import NewEnergyInformation from '../EnergyInformation/NewEnergyInformation';
import Heading from '../Heading';
import HighDemandFlag from '../HighDemand/HighDemandFlag';
import HighDemandMessaging from '../HighDemand/HighDemandMessaging';
import PricePod from '../PricePod/PricePod@default';
import ProductImage from '../ProductImage/ProductImage';
import PromoFlag from '../PromoFlag';
import ReviewRating from '../ReviewRating/ReviewRating';
import StockStatus from '../StockStatus';
import TradingRangeFlag from '../TradingRangeFlag/TradingRangeFlag';
import MobileDeliveryMessageBlock from './MobileDeliveryMessage/MobileDeliveryMessageBlock@default';
import MobileProductCardSkeleton from './Skeletons/MobileProductCardSkeleton';
import AddToBasketButton from '../../ProductCard/AddToBasket/AddToBasketButton@mobile';

const BackInStockForm = loadable(() =>
	import(/* webpackPrefetch: true */ '../BackInStockForm/BackInStockForm.js')
);

const RecommendationCarousel = loadable(() =>
	import('../RecommendationCarousel/RecommendationCarousel')
);

export const MobileProductCard = ({ index, product, observer, page }) => {
	const card = useRef(null);

	const [contextState, contextDispatch] = useContext(Context);
	const { loaded } = contextState;

	const {
		ListerAlternativeProductsEnabled,
		ListerProductCardEnergyLabelEnabled,
	} = useSelector((state) => state.experimentSwitches);

	const dispatch = useDispatch();

	const isSkuHighDemand = useIsProductInHighDemand(product);

	const isBelowXSmallOnly = useIsBelowXSmallOnly();

	useEventListener(`lazy-load-${product.ColourId}`, () => {
		contextDispatch({ type: 'UPDATE_LOADED', payload: true });
	});

	useEffect(() => {
		const cardRef = card.current;

		if (observer && cardRef) {
			observer.observe(cardRef);
		}

		return () => {
			if (observer && cardRef) {
				observer.unobserve(cardRef);
			}
		};
	}, [card.current, observer]);

	if (!product) {
		return null;
	}

	const tradingRangeInfo = isProductInTradingRange(product.Code);

	const deliveryDate = getDeliveryDate(
		product.DynamicDeliveryBulletsViewModel[0]
	);

	const deliveryDaysDifference = getDeliveryDaysDifference(deliveryDate);

	const theme = useTheme('aoLoves');

	if (!loaded) {
		return <MobileProductCardSkeleton ColourId={product.ColourId} />;
	}

	return (
		<li
			ref={card}
			data-page={page}
			id={product.Code}
			className="xs:p-2 w-full mobile-outer"
			style={{ WebkitTransform: 'translateZ(0)' }}
			data-testid={
				product.IsFeaturedProduct
					? 'product-card--featured'
					: 'product-card--standard'
			}>

			<div
				className={classnames('product-card', 'mobile-border', {
					'product-card--out-of-stock': product.State === 'OutOfStock',
					'flex flex-col h-full bg-darkgreen border-t-0 pb-2 px-2 relative overflow-hidden xs:rounded-lg':
						product.IsFeaturedProduct,
				})}
				style={{
					WebkitTransform: 'translateZ(0)',
					...(product.IsFeaturedProduct && {
						backgroundImage: theme.mobileBackground,
						backgroundSize: 'cover',
					}),
				}}
				data-testid={
					product.State === 'OutOfStock'
						? 'product-state--out-of-stock'
						: 'product-state--available'
				}>
				{product.IsFeaturedProduct && (
					<React.Fragment>
						<div className="flex py-3">
							<div className="ico ico-ao-loves text-white text-xl mr-1"></div>
							<div className="text-title-lg text-white px-1 leading-none">
								AO Loves
							</div>
						</div>
					</React.Fragment>
				)}

				<a
					href={product.FullProductUrl}
					onClick={(event) => {
						event.preventDefault();
						window.location.assign(product.FullProductUrl);

						dispatch(listerClickThrough(product.Code));
					}}
					className="product-card__link"
					data-testid="product-card-link"
					data-tag-name="lister product card"
					data-tag-type={product.IsFeaturedProduct ? 'featured' : 'standard'}
					data-tag-delivery-date={deliveryDate}
					data-tag-delivery-days={deliveryDaysDifference}>
					<span>
						{TRANSLATIONS.productList.productCard.viewProductAccessibilityText}
					</span>
				</a>

				<div
					className={classnames(
						'mobile-border',
						'flex flex-col bg-white p-4 xs:rounded-lg relative',
						{
							'trading-range--enabled pt-6 sm:pt-8': tradingRangeInfo,
							'border border-solid border-gray-30': !product.IsFeaturedProduct,
						}
					)}
					style={{ flex: '1' }}
					data-productcode={product.Code}
					data-testid="product-card-mobile-view">
					{tradingRangeInfo && <TradingRangeFlag {...tradingRangeInfo} small />}
					<div className="flex">
						<div className="product-card__mobile-image-col flex flex-col justify-between w-1/3 p-2 pl-0">
							<div>
								<ProductImage {...product} />
								<StockStatus
									classes="mt-4"
									stockStatus={product.State}
									productCode={product.Code}
									alignCenter
								/>

								{isSkuHighDemand && <HighDemandFlag />}

								{product.EnergyRating && (
									<EnergyInformation
										classes="mt-4 text-center z-10"
										energyRatingDetails={product.EnergyRatingDetails}
									/>
								)}
							</div>

							{!isSkuHighDemand && (
								<MobileDeliveryMessageBlock
									deliveryMessage={product.DynamicDeliveryBulletsViewModel[0]}
									stockStatus={product.State}
								/>
							)}
						</div>

						<div className="w-2/3 p-2 pr-0">
							<Heading content={product.FullTitle} />
							<ReviewRating classes="mt-2" {...product} />

							<PricePod
								product={product}
								viewModel={product.PricePodViewModel}
								inStock={product.State !== 'OutOfStock'}
								{...product}
							/>

							{ListerProductCardEnergyLabelEnabled && (
								<NewEnergyInformation
									energyRatingDetails={product.EnergyRatingDetails}
								/>
							)}

							<PromoFlag {...product} />
							<Bullets content={product.FeatureDescriptions} />
						</div>
					</div>

					<div className="z-10 mt-auto">
						{isSkuHighDemand && <HighDemandMessaging classes="mb-2" />}

						{product.State === 'OutOfStock' && (
							<BackInStockForm
								productCode={product.Code}
								productUrl={product.ProductUrl}
								productColourId={product.ColourId}
								productState={product.State}
							/>
						)}

						{ListerAlternativeProductsEnabled &&
							isBelowXSmallOnly &&
							product.State === 'OutOfStock' && (
								<RecommendationCarousel
									NowPrice={product.PricePodViewModel.NowPrice}
								/>
							)}
						{product.State !== 'OutOfStock' && !isSkuHighDemand && (
							<AddToBasketButton {...product} />
						)}
					</div>
				</div>
			</div>
		</li>
	);
};

export default MobileProductCard;
